import { mediaIdImageField } from '@/lib/mediaImageField';
import { AttractionImageCell, AttractionStatusBadge } from '@/components/attractions';
import { GreaterAreaDropdown, NeighborhoodDropdown, StateField } from '@/components/dropdowns';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import { Address, type CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';

// Define type based on our Prisma schema
export interface Attraction {
  id: string;
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  enrichedStatus?: 'pending' | 'enriched' | 'no_match' | 'error';
  description?: string;
  voiceoverId?: string;
  longDescription?: string;
  longVoiceoverId?: string;
  featuredImageId?: string;
  // Contact Information
  phoneNumber?: string;
  email?: string;
  website?: string;
  // Location & Relationships
  greaterAreaId: string;
  neighborhoodId?: string;
  attractionTypeId?: string;
  attractionCategoryId?: string;
  // Address Information
  country: string;
  state: string;
  city: string;
  streetAddress: string;
  streetAddress2?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  createdAt: string;
  updatedAt: string;
  // Relations for display
  greaterArea?: {
    id: string;
    name: string;
  };
  neighborhood?: {
    id: string;
    name: string;
  };
  voiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  longVoiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
  // Computed/display fields
  address?: Attraction;
  // Sync status fields (computed by API)
  isDescriptionVoiceoverInSync?: boolean | null;
  isLongDescriptionVoiceoverInSync?: boolean | null;
  // True when the saved address+coordinates have been confirmed against an
  // external source. The source is intentionally not exposed by the API.
  isAddressVerified?: boolean;
  // TourAttraction join table data (when fetched from tour attractions endpoint)
  tourAttraction?: {
    id: string;
    order: number;
    attractionThemeId?: string | null;
    attractionTheme?: {
      id: string;
      name?: string | null;
      themeType?: {
        id: string;
        name: string;
        slug: string;
      };
    } | null;
  } | null;
  // Themes array (when fetched from tour attractions endpoint)
  themes?: Array<{
    id: string;
    name?: string | null;
    themeType?: {
      id: string;
      name: string;
      slug: string;
    };
  }>;
  // Virtual field for inline theme editing on tour detail page
  attractionTheme?: unknown;
}

/**
 * Attraction CRUD configuration
 */
export const attractionCrudConfig: CrudConfig<Attraction> = {
  entityName: 'Attraction',
  entityNamePlural: 'Attractions',

  endpoints: {
    list: '/attractions',
    single: (id: string) => `/attractions/${id}`,
    create: '/attractions',
    update: (id: string) => `/attractions/${id}`,
    delete: (id: string) => `/attractions/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter attraction name',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'published',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Needs Review', value: 'needs_review' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <AttractionStatusBadge status={value as Attraction['status']} />;
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'voiceoverId',
      label: 'Description Voiceover',
      type: 'audio',
      required: false,
      helpText: 'Audio media ID for description voiceover',
    },
    {
      key: 'voiceover',
      label: 'Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: Attraction['voiceover'], fullRecord?: Attraction) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isDescriptionVoiceoverInSync ?? null}
            outOfSyncMessage="Description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'longDescription',
      label: 'Long Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter detailed description',
      validation: {
        max: 5000,
      },
    },
    {
      key: 'longVoiceoverId',
      label: 'Long Description Voiceover',
      type: 'audio',
      required: false,
      helpText: 'Audio media ID for long description voiceover',
    },
    {
      key: 'longVoiceover',
      label: 'Long Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: Attraction['longVoiceover'], fullRecord?: Attraction) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isLongDescriptionVoiceoverInSync ?? null}
            outOfSyncMessage="Long description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      ...mediaIdImageField,
      required: false,
      helpText: 'Upload attraction featured image (max 5MB)',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: Attraction['featuredImage'], fullRecord?: Attraction) => {
        return <AttractionImageCell attraction={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: Attraction['featuredImage'], fullRecord?: Attraction) => {
        return <AttractionImageCell attraction={fullRecord} size="lg" />;
      },
    },
    // Contact Information
    {
      key: 'phoneNumber',
      label: 'Phone Number',
      type: 'text',
      required: false,
      placeholder: 'Phone Number',
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email',
      required: false,
      placeholder: 'Email Address',
    },
    {
      key: 'website',
      label: 'Website',
      type: 'text',
      required: false,
      placeholder: 'Website URL',
      validation: {
        custom: (value: string) => {
          if (!value) return null;
          // Simple URL validation - must look like a URL
          if (!/^(https?:\/\/)?[\w.-]+\.\w{2,}/.test(value)) {
            return 'Please enter a valid URL (e.g., https://example.com)';
          }
          return null;
        },
      },
    },
    // Location & Relationships
    {
      key: 'greaterAreaId',
      label: 'Greater Area',
      type: 'custom',
      required: true,
      placeholder: 'Select Greater Area',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <GreaterAreaDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
    },
    {
      key: 'greaterArea',
      label: 'Greater Area',
      type: 'text',
      required: false,
      placeholder: 'Greater Area',
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/greater-areas/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'neighborhoodId',
      label: 'Neighborhood',
      type: 'custom',
      required: false,
      placeholder: 'Select Neighborhood',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
        formData?: Record<string, unknown>,
      ) => {
        const greaterAreaId = formData?.greaterAreaId as string | undefined;
        return (
          <NeighborhoodDropdown
            value={value}
            onChange={onChange}
            placeholder={field.placeholder}
            greaterAreaId={greaterAreaId}
          />
        );
      },
      dependsOn: ['greaterAreaId'],
    },
    {
      key: 'neighborhood',
      label: 'Neighborhood',
      type: 'text',
      required: false,
      placeholder: 'Neighborhood',
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/neighborhoods/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    // Address Information
    {
      key: 'country',
      label: 'Country',
      type: 'text',
      required: true,
      defaultValue: 'US',
      placeholder: 'Country',
    },
    {
      key: 'state',
      label: 'State',
      type: 'custom',
      required: true,
      placeholder: 'Select State',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
        formData?: Record<string, unknown>,
      ) => (
        <StateField
          value={value}
          onChange={onChange}
          placeholder={field.placeholder}
          country={formData?.country as string | undefined}
        />
      ),
    },
    {
      key: 'city',
      label: 'City',
      type: 'text',
      required: true,
      placeholder: 'City',
    },
    {
      key: 'streetAddress',
      label: 'Street Address',
      type: 'text',
      required: true,
      placeholder: 'Street Address',
    },
    {
      key: 'streetAddress2',
      label: 'Address Line 2',
      type: 'text',
      required: false,
      placeholder: 'Apartment, suite, etc.',
    },
    {
      key: 'postalCode',
      label: 'Postal Code',
      type: 'text',
      required: false,
      placeholder: 'Postal Code',
    },
    // Address display field
    {
      key: 'address',
      label: '',
      type: 'custom',
      required: false,
      format: (_value: unknown, fullRecord?: Attraction) => {
        if (!fullRecord) return null;
        return (
          <Address
            streetAddress={fullRecord.streetAddress}
            streetAddress2={fullRecord.streetAddress2}
            city={fullRecord.city}
            state={fullRecord.state}
            postalCode={fullRecord.postalCode}
            country={fullRecord.country}
          />
        );
      },
    },
    // Coordinates
    {
      key: 'latitude',
      label: 'Latitude',
      type: 'number',
      required: false,
      readonly: true,
    },
    {
      key: 'longitude',
      label: 'Longitude',
      type: 'number',
      required: false,
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: [
      'featuredImage',
      'name',
      'status',
      'greaterArea',
      'neighborhood',
      'createdAt',
      'updatedAt',
    ],
    searchableFields: ['name', 'city', 'state', 'streetAddress'],
    sortableFields: ['name', 'status', 'city', 'state', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    filters: [
      // Status & Enrichment
      {
        key: 'status',
        label: 'Status',
        type: 'multiselect',
        options: [
          { label: 'Published', value: 'published' },
          { label: 'Draft', value: 'draft' },
          { label: 'Needs Review', value: 'needs_review' },
        ],
      },
      {
        key: 'enrichedStatus',
        label: 'Enriched Status',
        type: 'multiselect',
        options: [
          { label: 'Pending', value: 'pending' },
          { label: 'Enriched', value: 'enriched' },
          { label: 'No Match', value: 'no_match' },
          { label: 'Error', value: 'error' },
        ],
      },
      // Location — Relations
      {
        key: 'greaterAreaId',
        label: 'Greater Area',
        type: 'relation',
        endpoint: '/greater-areas',
      },
      {
        key: 'neighborhoodId',
        label: 'Neighborhood',
        type: 'relation',
        endpoint: '/neighborhoods',
      },
      // Location — Text
      {
        key: 'city',
        label: 'City',
        type: 'text',
      },
      {
        key: 'state',
        label: 'State',
        type: 'text',
      },
      {
        key: 'country',
        label: 'Country',
        type: 'text',
      },
      {
        key: 'postalCode',
        label: 'Postal Code',
        type: 'text',
      },
      {
        key: 'streetAddress',
        label: 'Street Address',
        type: 'text',
      },
      // Content
      {
        key: 'name',
        label: 'Name',
        type: 'text',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'text',
      },
      {
        key: 'longDescription',
        label: 'Long Description',
        type: 'text',
      },
      // Contact
      {
        key: 'phoneNumber',
        label: 'Phone Number',
        type: 'text',
      },
      {
        key: 'email',
        label: 'Email',
        type: 'text',
      },
      {
        key: 'website',
        label: 'Website',
        type: 'text',
      },
      // Relationships
      {
        key: 'attractionTypeId',
        label: 'Attraction Type',
        type: 'relation',
        endpoint: '/attraction-types',
      },
      {
        key: 'attractionCategoryId',
        label: 'Attraction Category',
        type: 'relation',
        endpoint: '/attraction-categories',
      },
      // Dates
      {
        key: 'createdAt',
        label: 'Created Date',
        type: 'daterange',
      },
      {
        key: 'updatedAt',
        label: 'Updated Date',
        type: 'daterange',
      },
    ],
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core attraction details',
        fields: [
          'featuredImageLarge',
          'status',
          'voiceover',
          'description',
          'longVoiceover',
          'longDescription',
        ],
        defaultExpanded: true,
      },
      {
        title: 'Contact Information',
        description: 'Contact details for the attraction',
        fields: ['phoneNumber', 'email', 'website'],
        defaultExpanded: true,
      },
      {
        title: 'Location',
        description: 'Geographic and location details',
        fields: ['greaterArea', 'neighborhood'],
        defaultExpanded: true,
      },
      {
        title: 'Address Information',
        description: 'Physical address details',
        fields: ['address'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps, coordinates and system data',
        fields: ['latitude', 'longitude', 'createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Attraction',
        fields: ['name', 'status', 'featuredImageId', 'description', 'longDescription'],
        columns: 1,
      },
      {
        title: 'Contact',
        description: 'Contact Information for the Attraction',
        fields: ['phoneNumber', 'email', 'website'],
        columns: 2,
      },
      {
        title: 'Location',
        description: 'Location Information for the Attraction',
        fields: ['greaterAreaId', 'neighborhoodId'],
        columns: 2,
      },
      {
        title: 'Address',
        description: 'Address Information for the Attraction',
        fields: ['streetAddress', 'streetAddress2', 'city', 'state', 'postalCode', 'country'],
        columns: 2,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 2,
        max: 200,
      },
      status: {
        required: true,
      },
      greaterAreaId: {
        required: true,
      },
      country: {
        required: true,
      },
      state: {
        required: true,
      },
      city: {
        required: true,
      },
      streetAddress: {
        required: true,
      },
    },
  },
};
